import { useState, useEffect } from "react";
import axios from "axios";
import { BACKEND_URL } from "../App";
import { toast } from "sonner";
import { MessageCircle, Tag, DollarSign, ExternalLink } from "lucide-react";

const CatalogoPublico = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState("todos");

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/catalogo/publico`);
        setItems(res.data);
      } catch {
        toast.error("Error al cargar el catalogo");
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, []);

  const categorias = [...new Set(items.map(i => i.categoria))];

  const filtered = filterCat === "todos"
    ? items
    : items.filter(i => i.categoria === filterCat);

  const contactWhatsApp = (item) => {
    const msg = `Hola, me interesa: *${item.nombre}* ($${item.precio?.toLocaleString()} MXN)`;
    window.open(`https://wa.me/529993388933?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl text-emerald-900 tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Celebra<span style={{fontWeight: 700}}>Mid</span>
            </h1>
            <p className="text-xs text-stone-500">Catálogo de Servicios</p>
          </div>
          <a
            href="https://wa.me/529993388933"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-full text-sm font-medium hover:bg-green-700 transition-colors"
            data-testid="btn-contactar-whatsapp"
          >
            <MessageCircle className="w-4 h-4" />
            Contactar
          </a>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Hero */}
        <div className="text-center mb-10">
          <h2 className="font-display text-3xl sm:text-4xl text-stone-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Nuestros Servicios
          </h2>
          <p className="text-stone-600 max-w-xl mx-auto">
            Invitaciones digitales personalizadas, decoración, montaje y todo lo que necesitas para tu evento perfecto.
          </p>
        </div>

        {/* Filters */}
        {categorias.length > 1 && (
          <div className="flex flex-wrap gap-2 justify-center mb-8">
            <button
              onClick={() => setFilterCat("todos")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filterCat === "todos"
                  ? "bg-emerald-900 text-white"
                  : "bg-white border border-stone-200 text-stone-600 hover:border-emerald-300"
              }`}
              data-testid="filter-cat-todos"
            >
              Todos
            </button>
            {categorias.map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCat(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  filterCat === cat
                    ? "bg-emerald-900 text-white"
                    : "bg-white border border-stone-200 text-stone-600 hover:border-emerald-300"
                }`}
                data-testid={`filter-cat-${cat}`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map(item => (
              <div
                key={item.item_id}
                className="bg-white rounded-xl border border-stone-200 overflow-hidden hover:shadow-xl transition-all group"
                data-testid={`pub-item-${item.item_id}`}
              >
                <div className="aspect-square bg-stone-100 overflow-hidden relative">
                  {item.imagen_url ? (
                    <img
                      src={item.imagen_url}
                      alt={item.nombre}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Tag className="w-12 h-12 text-stone-300" />
                    </div>
                  )}
                  <div className="absolute top-3 right-3">
                    <span className="bg-white/90 backdrop-blur text-xs px-2 py-1 rounded-full text-stone-600 font-medium">
                      {item.categoria}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-medium text-stone-900 text-lg mb-1">{item.nombre}</h3>
                  {item.descripcion && (
                    <p className="text-sm text-stone-500 mb-4 line-clamp-3">{item.descripcion}</p>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4 text-emerald-600" />
                      <span className="font-display text-xl text-emerald-900">{item.precio?.toLocaleString()}</span>
                      <span className="text-xs text-stone-400 ml-1">MXN</span>
                    </div>
                    <button
                      onClick={() => contactWhatsApp(item)}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-full text-sm font-medium hover:bg-green-700 transition-colors"
                      data-testid={`btn-contactar-${item.item_id}`}
                    >
                      <MessageCircle className="w-4 h-4" />
                      Pedir
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Tag className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl text-stone-700 mb-2">No hay productos disponibles</h3>
            <p className="text-stone-500">El catálogo se actualizará pronto</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-stone-200 mt-16 py-8 text-center">
        <p className="text-sm text-stone-500">
          CelebraMid &mdash; Gestión de Eventos
        </p>
        <a
          href="https://wa.me/529993388933"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 text-sm mt-2"
        >
          <MessageCircle className="w-4 h-4" />
          WhatsApp: 999 338 8933
        </a>
      </footer>
    </div>
  );
};

export default CatalogoPublico;
